import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowRight } from "lucide-react";

export const SITE_URL = "https://lamaisondetasorcierepreferee.com";
const BRAND = "La Maison de ta Sorcière Préférée";

export function usePageTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} | ${BRAND}` : `${BRAND} | Noëmie Sartore`;
  }, [title]);
}

function _upsertMeta(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  return el;
}

export function usePageSeo({ title, description, path }) {
  useEffect(() => {
    const full = title ? `${title} | ${BRAND}` : `${BRAND} | Noëmie Sartore`;
    document.title = full;
    const url = SITE_URL + (path || window.location.pathname);
    if (description) {
      _upsertMeta('meta[name="description"]', () => Object.assign(document.createElement("meta"), { name: "description" })).setAttribute("content", description);
      _upsertMeta('meta[property="og:description"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:description"); return m; }).setAttribute("content", description);
    }
    _upsertMeta('meta[property="og:title"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:title"); return m; }).setAttribute("content", full);
    _upsertMeta('meta[property="og:type"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:type"); return m; }).setAttribute("content", "website");
    _upsertMeta('meta[property="og:url"]', () => { const m = document.createElement("meta"); m.setAttribute("property", "og:url"); return m; }).setAttribute("content", url);
    _upsertMeta('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" })).setAttribute("href", url);
  }, [title, description, path]);
}

export function JsonLd({ data }) {
  const payload = JSON.stringify(data);
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-jsonld", "page");
    s.text = payload;
    document.head.appendChild(s);
    return () => { s.remove(); };
  }, [payload]);
  return null;
}


export function SectionTitle({ eyebrow, title, sub, center = false, className = "" }) {
  return (
    <div className={`${center ? "text-center mx-auto" : ""} max-w-2xl ${className}`}>
      {eyebrow && <div className="eyebrow mb-3 !text-[#DFC07B]">{eyebrow}</div>}
      <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] font-medium tracking-tight text-[#F9F5EE] leading-[1.15]">
        {title}
      </h2>
      {sub && <p className="mt-4 text-base sm:text-lg text-[#CBB9CF] leading-relaxed">{sub}</p>}
      <div className={`gold-divider mt-6 w-24 ${center ? "mx-auto" : ""}`} />
    </div>
  );
}

export function MedicalWarning({ text, compact = false, tone = "dark" }) {
  const textColor = tone === "light" ? "text-[#E7DCEA]" : "text-[#7A5D58]";
  const iconColor = tone === "light" ? "text-[#DFC07B]" : "text-[#8a6a2f]";
  return (
    <div
      className={`rounded-2xl border border-[#C9A56A]/40 bg-[#C9A56A]/10 ${compact ? "p-4" : "p-5 sm:p-6"} flex gap-3`}
      data-testid="medical-warning"
    >
      <ShieldAlert className={`${iconColor} shrink-0 mt-0.5`} size={compact ? 18 : 20} />
      <p className={`${textColor} ${compact ? "text-xs" : "text-sm"} leading-relaxed`}>{text}</p>
    </div>
  );
}

export function TextBlock({ text, className = "", tone = "light" }) {
  const color = tone === "dark" ? "text-[#7A5D58]" : "text-[#E7DCEA]";
  return (
    <div className={className}>
      {text.split("\n\n").map((p, i) => (
        <p key={i} className={`${color} leading-[1.85] mb-4 whitespace-pre-line`}>
          {p}
        </p>
      ))}
    </div>
  );
}

export function CTALink({ to, children, testid, variant = "primary", className = "" }) {
  return (
    <Link to={to} data-testid={testid} className={`${variant === "primary" ? "btn-primary" : "btn-secondary"} ${className}`}>
      {children}
      <ArrowRight size={16} />
    </Link>
  );
}
